<template>
  <teleport to="body">
    <!-- Scrim -->
    <transition name="fade">
      <div v-if="isOpen" class="notification-scrim" @click="notificationStore.closePanel()" />
    </transition>

    <!-- Panel -->
    <transition name="slide">
      <div v-if="isOpen" class="notification-panel" :style="{ width: panelWidth }">
        <div class="d-flex flex-column h-100" style="background-color: #ffffff; border: 1px solid #d0d0d0">
          <!-- Top nav -->
          <div
            class="d-flex align-center justify-space-between"
            style="padding: 24px; height: 104px; background-color: #ffffff"
          >
            <!-- Back -->
            <button
              class="d-flex align-center"
              style="gap: 8px; background: none; border: none; cursor: pointer; padding: 0"
              @click="handleBack"
            >
              <v-icon size="24">mdi-chevron-left</v-icon>
              <span
                style="
                  font-family: Arial, sans-serif;
                  font-weight: 700;
                  font-size: 16px;
                  line-height: 24px;
                  letter-spacing: -0.48px;
                  color: #000000;
                  text-decoration: underline;
                "
              >
                Back
              </span>
            </button>

            <!-- Notifications + badge -->
            <div class="d-flex align-center" style="gap: 4px">
              <span
                style="
                  font-family: Arial, sans-serif;
                  font-weight: 700;
                  font-size: 20px;
                  line-height: 26px;
                  letter-spacing: -0.6px;
                  color: #000000;
                "
              >
                Notifications
              </span>
              <div
                v-if="notificationStore.unreadCount > 0"
                class="d-flex align-center justify-center flex-shrink-0"
                style="width: 24px; height: 24px; border-radius: 100px; background-color: #cc2f2f"
              >
                <span
                  style="
                    font-family: Arial, sans-serif;
                    font-weight: 700;
                    font-size: 12px;
                    line-height: 18px;
                    color: #ffffff;
                  "
                >
                  {{ notificationStore.unreadCount }}
                </span>
              </div>
            </div>

            <!-- Expand -->
            <button
              style="background: none; border: none; cursor: pointer; padding: 0; display: flex; align-items: center"
              @click="handleExpand"
            >
              <v-icon size="24">mdi-arrow-expand</v-icon>
            </button>
          </div>

          <!-- Announcements section -->
          <div
            class="d-flex flex-column flex-grow-1"
            style="background-color: #ffffff; border-top: 1px solid #d0d0d0; overflow: hidden"
          >
            <!-- Sub-header -->
            <div class="d-flex align-center justify-space-between" style="padding: 24px 24px 12px 24px">
              <span
                style="
                  font-family: Arial, sans-serif;
                  font-weight: 700;
                  font-size: 20px;
                  line-height: 26px;
                  letter-spacing: -0.6px;
                  color: #747474;
                "
              >
                Most recent
              </span>
              <button
                v-if="notificationStore.hasUnread"
                style="
                  font-family: Arial, sans-serif;
                  font-size: 14px;
                  line-height: 18px;
                  color: #000000;
                  background: none;
                  border: none;
                  cursor: pointer;
                  padding: 0;
                "
                @click="notificationStore.markAllAsRead()"
              >
                Mark all as read
              </button>
            </div>

            <!-- Notification list -->
            <div ref="scrollContainer" class="flex-grow-1 overflow-y-auto" style="padding: 0 24px">
              <div v-if="notifications.length === 0" class="d-flex align-center justify-center" style="padding: 48px 0">
                <span style="font-family: Arial, sans-serif; font-size: 16px; line-height: 24px; color: #747474">
                  No notifications
                </span>
              </div>

              <template v-else>
                <div v-for="(notification, index) in notifications" :key="notification.id">
                  <NotificationItem :notification="notification" />
                  <div
                    v-if="index < notifications.length - 1"
                    style="height: 0; width: 100%; border-bottom: 1px solid #d0d0d0"
                  />
                </div>
              </template>
            </div>
          </div>
        </div>
      </div>
    </transition>
  </teleport>
</template>

<script>
import { useDisplay } from 'vuetify';
import { useNotificationStore } from '@/stores/notifications';
import { DataLayerMixin } from '../../mixins/dataLayerMixin';
import NotificationItem from './NotificationItem.vue';

export default {
  name: 'NotificationPanel',
  mixins: [DataLayerMixin],
  components: { NotificationItem },
  setup() {
    const notificationStore = useNotificationStore();
    const { mobile } = useDisplay();
    return { notificationStore, mobile };
  },
  data() {
    return {
      observer: null,
      pendingReadTimers: new Map()
    };
  },
  computed: {
    isOpen() {
      return this.notificationStore.panelOpen && !this.notificationStore.panelExpanded;
    },
    panelWidth() {
      return this.mobile ? '100%' : '510px';
    },
    notifications() {
      return this.notificationStore.allNotifications;
    }
  },
  watch: {
    isOpen(open) {
      if (open) {
        this.$nextTick(() => this.initScrollObserver());
      } else {
        this.cleanupObserver();
      }
    }
  },
  beforeUnmount() {
    this.cleanupObserver();
  },
  methods: {
    handleBack() {
      this.asuoDataLayerClick('accelerateDashboard', '37');
      this.notificationStore.closePanel();
    },
    handleExpand() {
      this.asuoDataLayerClick('accelerateDashboard', '38');
      this.notificationStore.panelExpanded = true;
    },
    initScrollObserver() {
      this.cleanupObserver();

      const container = this.$refs.scrollContainer;
      if (!container) return;

      this.observer = new IntersectionObserver(
        (entries) => {
          entries.forEach((entry) => {
            const id = entry.target.dataset?.notificationId;
            if (!id) return;

            if (entry.isIntersecting && !this.notificationStore.readIds.has(id)) {
              this.pendingReadTimers.set(
                id,
                setTimeout(() => {
                  this.notificationStore.markAsRead(id);
                  this.pendingReadTimers.delete(id);
                }, 3000)
              );
            } else if (!entry.isIntersecting) {
              clearTimeout(this.pendingReadTimers.get(id));
              this.pendingReadTimers.delete(id);
            }
          });
        },
        {
          root: container,
          threshold: 0.8
        }
      );

      container.querySelectorAll('[data-notification-id]').forEach((el) => {
        this.observer.observe(el);
      });
    },

    cleanupObserver() {
      if (this.observer) {
        this.observer.disconnect();
        this.observer = null;
      }
      this.pendingReadTimers.forEach((timer) => clearTimeout(timer));
      this.pendingReadTimers.clear();
    }
  }
};
</script>

<style scoped>
.notification-scrim {
  position: fixed;
  top: 0;
  left: 0;
  right: 0;
  bottom: 0;
  background-color: rgba(0, 0, 0, 0.5);
  z-index: 2000;
}

.notification-panel {
  position: fixed;
  top: 0;
  right: 0;
  bottom: 0;
  z-index: 2001;
  overflow: hidden;
}

.slide-enter-active,
.slide-leave-active {
  transition: transform 0.3s ease;
}

.slide-enter-from,
.slide-leave-to {
  transform: translateX(100%);
}

.fade-enter-active,
.fade-leave-active {
  transition: opacity 0.3s ease;
}

.fade-enter-from,
.fade-leave-to {
  opacity: 0;
}
</style>
